import { CaretDownIcon } from '@phosphor-icons/react';
import { useId, useMemo, useRef, useState } from 'react';
import type { KeyboardEvent, ReactNode, RefObject } from 'react';
import { Command, CommandItem, CommandList } from '@voll-intelligence/ui-kit/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@voll-intelligence/ui-kit/ui/popover';
import { Button } from '@voll-intelligence/ui-kit/ui/button';
import { Spinner } from '@voll-intelligence/ui-kit/ui/spinner';
import {
  countryOptions,
  filterCountryOptions,
  flagOf,
  normalizeCountryCode,
  type CountryCatalogItem,
  type CountryOption,
  type CountryValue,
} from './country_options.js';

/**
 * O CAMPO DE PAÍS compartilhado (#3669): nacionalidade do perfil e do checkout,
 * país emissor do passaporte — um controle, um catálogo (o do voll-places).
 * Puro: recebe a lista, o valor e a copy; quem busca e cacheia é o host.
 *
 * Só o CONTROLE, sem rótulo: o checkout embrulha em `FieldShell`, o perfil em
 * `EditField`, e cada casca tem a própria métrica de rótulo. O nome acessível
 * vem por `copy.label`.
 *
 * A mecânica é a do `AsyncSelectField` do checkout (o Motivo): a casca é um
 * `div` que hospeda o input da busca, o popover do DS sobre `Command` com o
 * filtro feito aqui, e o input encaminha ↑↓/Enter pro root do cmdk. Catálogo
 * local não tem "buscando": a lista entra no mesmo render.
 *
 * FAIL-CLOSED: `unavailable` mostra o valor gravado só-leitura com o motivo,
 * nunca um select vazio que convidaria a apagar o que existe — e, com
 * `onRetry`, a ação de tentar de novo, para não deixar a pessoa sem saída
 * (#4302). `loading`
 * desabilita o gatilho mostrando o nome atual — nunca o código cru.
 *
 * A regra do `name` mora aqui e só aqui: escolher grava o `name` do catálogo;
 * o código gravado FORA do catálogo vira a primeira opção com o `name` já
 * gravado no perfil, e reescolhê-lo o preserva (#3651).
 */

export type CountryFieldState = 'ready' | 'loading' | 'unavailable';

export type CountryFieldCopy = {
  /** Nome acessível do campo ("Nacionalidade", "País emissor"). */
  readonly label: string;
  /** Sem valor: o que o campo ocioso mostra. */
  readonly placeholder: string;
  /** Aberto, sem termo digitado. */
  readonly searchPlaceholder: string;
  /** Filtro sem resultado. */
  readonly empty: string;
  /** Catálogo indisponível: a linha abaixo do valor só-leitura. */
  readonly unavailable: string;
  /** Legenda da opção gravada que o catálogo não conhece ("gravado no perfil"). */
  readonly outsideCatalog: string;
  /** Rótulo da ação de recarregar o catálogo indisponível (com `onRetry`). */
  readonly retry?: string;
};

export type CountryFieldProps = {
  readonly id?: string;
  readonly value?: CountryValue;
  readonly countries: readonly CountryCatalogItem[];
  readonly state: CountryFieldState;
  readonly copy: CountryFieldCopy;
  readonly disabled?: boolean;
  readonly onChange: (value: CountryValue) => void;
  /** Catálogo indisponível: recarrega. Só aparece com `copy.retry`. */
  readonly onRetry?: () => void;
};

/* Só as teclas que são DA LISTA. Home/End/PageUp ficam com o caret do input. */
const LIST_KEYS = new Set(['ArrowDown', 'ArrowUp', 'Enter']);

/** A veste do controle: a receita do `SelectTrigger` do DS (raio `lg`, borda
 *  `input`, anel de foco do kit) na régua de 36px dos dois hosts. O foco mora
 *  no input DE DENTRO, então o anel é `focus-within`. */
const CONTROL_CLASS =
  'flex h-9 w-full min-w-0 items-center gap-2 rounded-lg border border-input bg-transparent ' +
  'px-2.5 text-sm transition-colors focus-within:border-ring focus-within:ring-3 ' +
  'focus-within:ring-ring/50';

/** A bandeira é decorativa: o nome do país já está no texto ao lado. */
function Flag({ code }: { readonly code: string }): ReactNode {
  const flag = flagOf(code);
  if (flag === '') return null;
  return (
    <span aria-hidden="true" className="shrink-0 text-base leading-none">
      {flag}
    </span>
  );
}

/** Clique no campo com o popover ABERTO é posicionamento de cursor no input,
 *  não pedido de fechar. */
function closedByTriggerPress(details: { reason?: string } | undefined): boolean {
  return details?.reason === 'trigger-press';
}

/**
 * A casca do controle, pro `render` do `PopoverTrigger`. `<div>` e não
 * `<button>`: quem mora dentro é o `<input>` da busca. O gatilho é MUDO na
 * árvore de acessibilidade (`role`, `aria-haspopup` e `aria-expanded` do
 * Base UI ficam de fora) — quem fala é o input (o campo tem UM combobox).
 */
function ControlShell({
  disabled,
  role: _role,
  'aria-haspopup': _hasPopup,
  'aria-expanded': _expanded,
  ...rest
}: { readonly disabled: boolean } & Record<string, unknown>) {
  return (
    <div
      {...rest}
      tabIndex={-1}
      aria-disabled={disabled || undefined}
      className={`${CONTROL_CLASS}${disabled ? ' pointer-events-none opacity-50' : ''}`}
    />
  );
}

/**
 * O input do campo. Fechado é `readOnly` e mostra o rótulo da escolha; foco
 * NÃO abre (escolher devolve o foco pro gatilho e a lista reabriria sozinha).
 * Abre por clique ou ↓/Enter; aberto, encaminha ↑↓/Enter pro cmdk. Com a
 * lista ABERTA o Escape fecha SÓ este campo e morre aqui: no drawer do
 * checkout, um Esc que subisse derrubaria a camada de trás junto. FECHADO, o
 * Escape não é do campo e sobe — é ele que fecha o drawer.
 */
function SearchInput({
  id,
  label,
  listId,
  activeId,
  open,
  term,
  shown,
  placeholder,
  disabled,
  panelRef,
  onTerm,
  onOpen,
  onEscape,
}: {
  readonly id: string;
  readonly label: string;
  readonly listId: string;
  /** O `id` da opção destacada na lista, para o leitor de tela anunciar o que o
   *  Enter vai escolher — o foco fica AQUI, não no item do cmdk. */
  readonly activeId: string | undefined;
  readonly open: boolean;
  readonly term: string;
  readonly shown: string;
  readonly placeholder: string;
  readonly disabled: boolean;
  readonly panelRef: RefObject<HTMLElement | null>;
  readonly onTerm: (term: string) => void;
  readonly onOpen: () => void;
  readonly onEscape: () => void;
}): ReactNode {
  const forward = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Escape') {
      if (!open) return;
      e.preventDefault();
      e.stopPropagation();
      onEscape();
      return;
    }
    /* O Enter que confirma uma composição de IME é do input, não da lista: o
       evento reemitido abaixo não carrega `isComposing`, e o cmdk escolheria. */
    if (e.nativeEvent.isComposing) return;
    if (!LIST_KEYS.has(e.key)) return;
    if (!open) {
      e.preventDefault();
      onOpen();
      return;
    }
    const root = panelRef.current?.querySelector('[cmdk-root]');
    if (!root) return;
    e.preventDefault();
    root.dispatchEvent(new globalThis.KeyboardEvent('keydown', { key: e.key, bubbles: true }));
  };
  return (
    <input
      id={id}
      type="text"
      role="combobox"
      aria-expanded={open}
      aria-controls={open ? listId : undefined}
      aria-activedescendant={open ? activeId : undefined}
      aria-autocomplete="list"
      aria-label={label}
      disabled={disabled}
      readOnly={!open}
      placeholder={placeholder}
      value={open ? term : shown}
      onChange={(e) => {
        onTerm(e.target.value);
      }}
      onKeyDown={forward}
      className="w-full min-w-0 bg-transparent text-foreground outline-none placeholder:font-normal placeholder:text-muted-foreground"
    />
  );
}

function OptionRow({
  option,
  current,
  outsideCatalogCopy,
  onPick,
}: {
  readonly option: CountryOption;
  readonly current: boolean;
  readonly outsideCatalogCopy: string;
  readonly onPick: (option: CountryOption) => void;
}): ReactNode {
  return (
    <CommandItem
      value={option.code}
      aria-current={current ? 'true' : undefined}
      onSelect={() => {
        onPick(option);
      }}
    >
      <Flag code={option.code} />
      <span className="grid min-w-0 flex-1">
        <span className="truncate text-foreground">{option.label}</span>
        {/* Espaço-texto entre as linhas: no grid ele não gera caixa, mas entra
            no nome acessível — sem ele o leitor emenda rótulo e legenda. */}
        {option.outsideCatalog ? ' ' : ''}
        {option.outsideCatalog && (
          <span className="truncate text-xs text-muted-foreground">{outsideCatalogCopy}</span>
        )}
      </span>
      <span className="ml-auto shrink-0 text-xs text-muted-foreground tabular-nums">
        {option.code}
      </span>
    </CommandItem>
  );
}

/** The DOM id cmdk gave the item with this value (`undefined` for none). */
function cmdkItemId(panelId: string, value: string): string | undefined {
  if (value === '') return undefined;
  const item = document
    .getElementById(panelId)
    ?.querySelector<HTMLElement>(`[cmdk-item][data-value="${CSS.escape(value)}"]`);
  return item?.id || undefined;
}

/** Catálogo indisponível: o valor gravado como texto, com o motivo embaixo.
 *  `output` porque é um valor do formulário que a pessoa lê, não edita. */
function ReadOnlyValue({
  id,
  value,
  copy,
  onRetry,
}: {
  readonly id: string;
  readonly value: CountryValue | undefined;
  readonly copy: CountryFieldCopy;
  readonly onRetry: (() => void) | undefined;
}): ReactNode {
  const code = normalizeCountryCode(value?.code);
  const shown = value?.name.trim() || code;
  return (
    <div className="grid gap-1">
      <output
        id={id}
        aria-label={copy.label}
        className="flex h-9 min-w-0 items-center gap-2 text-sm text-foreground"
      >
        {code !== '' && <Flag code={code} />}
        <span className="truncate">{shown || copy.placeholder}</span>
      </output>
      <p className="text-xs text-muted-foreground">
        {copy.unavailable}
        {onRetry && copy.retry && (
          <>
            {' '}
            <Button
              type="button"
              variant="link"
              size="sm"
              className="h-auto p-0 text-xs"
              onClick={onRetry}
            >
              {copy.retry}
            </Button>
          </>
        )}
      </p>
    </div>
  );
}

export function CountryField({
  id,
  value,
  countries,
  state,
  copy,
  disabled = false,
  onChange,
  onRetry,
}: CountryFieldProps): ReactNode {
  const generatedId = useId();
  const fieldId = id ?? generatedId;
  const listId = `${generatedId}-list`;
  const panelId = `${generatedId}-panel`;
  const [open, setOpen] = useState(false);
  const [term, setTerm] = useState('');
  const panelRef = useRef<HTMLDivElement>(null);
  /* A opção DESTACADA pelo teclado (o `value` controlado do cmdk) — espelhada
     no `aria-activedescendant` do input. `''` = nenhuma. */
  const [highlighted, setHighlighted] = useState('');
  /* O `id` DOM do item destacado. O cmdk gera o `id` dos itens (e ignora um
     passado por prop), então ele é LIDO do painel pelo `data-value` — quando o
     cmdk avisa o destaque, o item já está no DOM. By the panel's id, not its
     ref: the first item is highlighted in cmdk's layout effect, before the
     panel's ref is attached. */
  const [activeId, setActiveId] = useState<string | undefined>(undefined);
  const highlight = (next: string) => {
    setHighlighted(next);
    setActiveId(cmdkItemId(panelId, next));
  };

  const options = useMemo(() => countryOptions(countries, value), [countries, value]);
  const savedCode = normalizeCountryCode(value?.code);
  const chosen = options.find((option) => option.code === savedCode);
  const shown = chosen?.label ?? value?.name.trim() ?? '';
  const filtered = useMemo(() => filterCountryOptions(options, term), [options, term]);

  if (state === 'unavailable')
    return <ReadOnlyValue id={fieldId} value={value} copy={copy} onRetry={onRetry} />;

  const locked = disabled || state === 'loading';
  const close = () => {
    setOpen(false);
    setTerm('');
    highlight('');
  };
  const openField = () => {
    if (locked || open) return;
    setTerm('');
    highlight('');
    setOpen(true);
  };
  const handleOpenChange = (next: boolean, details: { reason?: string } | undefined) => {
    if (next && locked) return;
    if (!next && closedByTriggerPress(details)) return;
    /* Zera o rascunho nas DUAS direções: reabrir sobre a busca anterior
       mostraria lista filtrada por algo que a pessoa não digitou agora. */
    setTerm('');
    highlight('');
    setOpen(next);
  };
  const pick = (option: CountryOption) => {
    onChange({ code: option.code, name: option.name });
    close();
  };

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger nativeButton={false} render={<ControlShell disabled={locked} />}>
        {!open && shown !== '' && <Flag code={savedCode} />}
        <SearchInput
          id={fieldId}
          label={copy.label}
          listId={listId}
          activeId={activeId}
          open={open}
          term={term}
          shown={shown}
          placeholder={open ? copy.searchPlaceholder : copy.placeholder}
          disabled={locked}
          panelRef={panelRef}
          onTerm={setTerm}
          onOpen={openField}
          onEscape={close}
        />
        {state === 'loading' ? (
          <Spinner className="size-4 shrink-0 text-muted-foreground" aria-label={copy.label} />
        ) : (
          <CaretDownIcon className="size-4 shrink-0 text-muted-foreground" aria-hidden="true" />
        )}
      </PopoverTrigger>
      {/* `w-(--anchor-width)`: a lista tem a largura do campo. `initialFocus`
          desligado mantém o foco no input DO CAMPO. */}
      <PopoverContent
        align="start"
        className="w-(--anchor-width) p-1"
        initialFocus={false}
        aria-label={copy.label}
      >
        <div ref={panelRef} id={panelId} className="contents">
          <Command
            shouldFilter={false}
            label={copy.label}
            className="bg-transparent p-0"
            value={highlighted}
            onValueChange={highlight}
          >
            <CommandList id={listId} className="max-h-64">
              {filtered.length > 0 ? (
                filtered.map((option) => (
                  <OptionRow
                    key={option.code}
                    option={option}
                    current={option.code === savedCode}
                    outsideCatalogCopy={copy.outsideCatalog}
                    onPick={pick}
                  />
                ))
              ) : (
                <div role="status" className="py-6 text-center text-sm text-muted-foreground">
                  {copy.empty}
                </div>
              )}
            </CommandList>
          </Command>
        </div>
      </PopoverContent>
    </Popover>
  );
}
